import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import './PrivacyPolicyModal.css';

interface Props {
  onClose: () => void;
}

export const PrivacyPolicyModal: React.FC<Props> = ({ onClose }) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      prev?.focus();
    };
  }, [onClose]);

  const modal = (
    <div className="ppm-backdrop" onClick={onClose} aria-hidden="true">
      <div
        className="ppm-card"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ppm-title"
        tabIndex={-1}
        onClick={e => e.stopPropagation()}
      >
        <div className="ppm-header">
          <h2 id="ppm-title" className="ppm-title">Privacy Policy</h2>
          <span className="ppm-updated">Updated 2026</span>
          <button className="ppm-close" onClick={onClose} aria-label="Close privacy policy">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className="ppm-body">

          <section className="ppm-section">
            <h3 className="ppm-section-title">1. Introduction &amp; Accountability</h3>
            <p>Gtarents.ca (&ldquo;we,&rdquo; &ldquo;us&rdquo;) is committed to protecting the privacy of our users in compliance with the <em>Personal Information Protection and Electronic Documents Act</em> (PIPEDA). We act as the data custodian for the information you provide to facilitate peer-to-peer rental matching.</p>
          </section>

          <section className="ppm-section">
            <h3 className="ppm-section-title">2. Information We Collect</h3>
            <p>We limit collection to the minimum data necessary to provide our matching service:</p>

            <p className="ppm-sub-heading">Identifiable Information</p>
            <ul className="ppm-list">
              <li><strong>Email Address:</strong> Required for all listings. This is our primary way to facilitate matches.</li>
              <li><strong>Phone Number:</strong> Optional. Provided only if you wish to allow matched users to call or text you.</li>
              <li><strong>IP Address:</strong> Auto-detected to prevent spam, fraud, and enforce platform bans.</li>
            </ul>

            <p className="ppm-sub-heading">Location &amp; Preference Data</p>
            <ul className="ppm-list">
              <li><strong>Coordinates:</strong> The location you place on the map. We round these to ~100m in our public API to protect your exact residence.</li>
              <li><strong>Rental Details:</strong> BHK, price, furnishing, and preferences. These are displayed publicly.</li>
            </ul>

            <p className="ppm-sub-heading">Historical Rent Data</p>
            <p>Optional. We collect the amount you currently pay to help create a community-driven &ldquo;Rent Map.&rdquo;</p>
            <ul className="ppm-list">
              <li><strong>Privacy:</strong> This value is displayed on the map but is never linked to your name, exact unit number, email, or phone number in public view.</li>
            </ul>

            <p className="ppm-sub-heading">Lifestyle Preferences</p>
            <p>We collect data regarding gender, smoking habits, and food preferences (e.g., vegetarian kitchens).</p>
            <ul className="ppm-list">
              <li><strong>Purpose:</strong> This data is collected strictly for matching purposes to ensure compatibility between seekers and existing households.</li>
              <li><strong>Non-Discrimination:</strong> We do not use this information to profile or discriminate against users. Providing this information is voluntary, though it may limit matching results if omitted.</li>
            </ul>

            <p className="ppm-sub-heading">Activity Data</p>
            <p>Reports and ratings are linked to your IP address to prevent duplicate reporting and ensure community safety.</p>
          </section>

          <section className="ppm-section">
            <h3 className="ppm-section-title">3. How We Use and Share Your Data (The Matching Pipeline)</h3>
            <p>The core function of Gtarents.ca is a consensual data exchange.</p>
            <ul className="ppm-list">
              <li><strong>The Match:</strong> Our system compares your pin against others based on budget, location, and lifestyle preferences.</li>
              <li><strong>The Disclosure:</strong> When a match is found, we send a single automated email to both parties containing each other&rsquo;s contact info.</li>
              <li><strong>Transparency:</strong> Your contact details are never visible on the public map. They are only shared with individuals who meet your specific criteria.</li>
              <li><strong>Quality Review:</strong> Match emails are cc&rsquo;d to our internal team solely for abuse prevention and quality assurance.</li>
              <li><strong>Market Insights:</strong> Aggregated rent data is used to provide newcomers with average pricing for specific neighbourhoods. We use &ldquo;coordinate rounding&rdquo; (~100m) to ensure individual apartments cannot be identified.</li>
            </ul>
          </section>

          <section className="ppm-section">
            <h3 className="ppm-section-title">4. Third-Party Service Providers</h3>
            <p>We use trusted third parties to power the platform. Your data is subject to their respective privacy policies:</p>
            <ul className="ppm-list">
              <li><strong>Google Maps:</strong> Powers map tiles and location searches.</li>
              <li><strong>Supabase (AWS Canada):</strong> Our database host. Data is stored on Canadian-region servers where possible to ensure local data residency.</li>
              <li><strong>Ipify.org:</strong> Used for IP lookup to verify session security.</li>
            </ul>
          </section>

          <section className="ppm-section">
            <h3 className="ppm-section-title">5. Data Retention &amp; Deletion</h3>
            <p>We do not keep your data longer than necessary:</p>
            <ul className="ppm-list">
              <li><strong>Active Pins:</strong> Kept until you delete them or they expire.</li>
              <li><strong>Seeker Pins:</strong> Auto-expire and are deleted after 30 days.</li>
              <li><strong>IP Logs:</strong> Retained for 30 days for security auditing, then anonymized.</li>
              <li><strong>Match History:</strong> Once an email is sent, the recipient possesses that contact info. While you can delete your data from our servers, we cannot &ldquo;unsend&rdquo; or recall emails already delivered to matched users.</li>
            </ul>
          </section>

          <section className="ppm-section">
            <h3 className="ppm-section-title">6. Your Rights &amp; Control</h3>
            <p>Under Canadian law, you have the right to access and correct your data.</p>
            <ul className="ppm-list">
              <li><strong>Self-Service:</strong> You can delete your pin at any time by clicking your pin on the map. This immediately stops any future matching.</li>
              <li><strong>Right to Erasure:</strong> To request a full purge of your data from our logs, email <a className="ppm-link" href="mailto:help@gtarents.ca">help@gtarents.ca</a>. We will respond to all requests within 30 days.</li>
            </ul>
          </section>

          <section className="ppm-section">
            <h3 className="ppm-section-title">7. Security Safeguards</h3>
            <p>We employ technical safeguards including HTTPS encryption for all transmissions, API rate-limiting to prevent scraping, and coordinate-rounding to protect user privacy. We do not use cookies for tracking and do not require passwords, reducing the risk of credential leaks.</p>
          </section>

        </div>

        <div className="ppm-footer">
          <button className="ppm-done-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};
